import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import type { MailboxLimitDetail } from '../utils/mailboxLimitMessaging';

export type MailboxLimitBlockedCalloutProps = {
    /** Server or proactive explanation (what / why / next). */
    body: string;
    detail?: MailboxLimitDetail | null;
    /** Optional usage line derived from structured fields. */
    usageLine?: string | null;
    className?: string;
};

const defaultClassName =
    'rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-theme flex flex-col gap-2';

const ctaClassName =
    'inline-flex items-center justify-center px-3 py-1 rounded-lg bg-amber-500 text-black font-semibold text-[11px] hover:bg-amber-400 transition-colors';

function isSameOriginHttpUrl(href: string): boolean {
    if (!/^https?:\/\//i.test(href)) {
        return false;
    }
    try {
        const u = new URL(href);
        if (typeof window === 'undefined' || !window.location?.origin) {
            return true;
        }
        return u.origin === window.location.origin;
    } catch {
        return false;
    }
}

/**
 * Explain-then-fallback for mailbox limits: persistent inline alert + Billing CTA (FR16, UX-DR20).
 */
const MailboxLimitBlockedCallout: React.FC<MailboxLimitBlockedCalloutProps> = ({
    body,
    detail,
    usageLine,
    className = defaultClassName,
}) => {
    const external = detail?.upgrade_url?.trim();
    const useExternal = Boolean(external && isSameOriginHttpUrl(external));

    return (
        <div className={className} role="status" aria-live="polite">
            <div className="flex items-start gap-2">
                <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" aria-hidden />
                <div className="min-w-0 space-y-1">
                    <div className="font-semibold text-theme">Connect another Gmail account</div>
                    <p className="text-theme leading-snug">{body}</p>
                    {usageLine ? <p className="text-theme-secondary">{usageLine}</p> : null}
                </div>
            </div>
            <div className="flex justify-end">
                {useExternal ? (
                    <a href={external} className={ctaClassName}>
                        Open Billing
                    </a>
                ) : (
                    <Link to="/app/billing" className={ctaClassName}>
                        Open Billing
                    </Link>
                )}
            </div>
        </div>
    );
};

export default MailboxLimitBlockedCallout;
